import { auth, db, isFirebaseActive, messaging } from './firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getToken } from 'firebase/messaging';
import { isAndroidTwa, isNativePlatform } from '../utils/capacitorBridge';

// Cloud Functions adresi (sabit run.app adresi yerine; fonksiyon yeniden oluşturulsa da bozulmaz)
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://europe-west1-tarih-atlas-kpss.cloudfunctions.net';

// Standart VAPID Public Key (Web Push bildirimlerini şifrelemek/kimlik doğrulamak için kullanılır)
export const VAPID_PUBLIC_KEY = 'BPJ2N9soRDyRjVB5dpukJ4c7eQbEDPAGVe_tk7527-hdlJsoCUYFHlPxFApWhVVfKcJxLSW6Gi3Kql-wOgkErfs';

/**
 * VAPID Public Key'ini Firestore'dan çekmeye çalışır, yoksa varsayılanı döner.
 */
export async function getVapidPublicKey(): Promise<string> {
    if (!isFirebaseActive) return VAPID_PUBLIC_KEY;
    try {
        const docSnap = await getDoc(doc(db, "secrets", "vapid_public"));
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && data.publicKey) {
                console.log("[NotificationService] VAPID public key loaded from Firestore.");
                return data.publicKey;
            }
        }
    } catch (error) {
        console.warn("[NotificationService] VAPID public key could not be loaded, using fallback:", error);
    }
    return VAPID_PUBLIC_KEY;
}

/**
 * Base64 VAPID anahtarını Uint8Array dizisine dönüştürür.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Kullanıcıdan anlık bildirim izinlerini ister.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
        console.warn("Bu cihaz/tarayıcı bildirimleri desteklemiyor.");
        return 'denied';
    }

    try {
        const permission = await Notification.requestPermission();
        console.log(`[NotificationService] İzin durumu: ${permission}`);
        return permission;
    } catch (error) {
        console.error("[NotificationService] İzin istenirken hata oluştu:", error);
        return 'denied';
    }
}

/**
 * Eski veya çakışan servis çalışanlarını (özellikle /firebase-cloud-messaging-push-scope altındaki firebase-messaging-sw.js) temizler.
 */
export async function cleanOldServiceWorkers(): Promise<void> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
            const scope = registration.scope || '';
            if (scope.includes('firebase-cloud-messaging-push-scope')) {
                console.log(`[NotificationService] Eski scope kaldırılıyor: ${scope}`);
                await registration.unregister();
            }
        }
    } catch (error) {
        console.warn("[NotificationService] Eski servis çalışanları temizlenirken hata oluştu:", error);
    }
}

/**
 * FCM Tokenını alır ve döner.
 * SW artık service-worker.js içinde Firebase Messaging ile birleştirilmiştir.
 */
export async function subscribeUserToFCM(): Promise<string | null> {
    if (!messaging) {
        console.warn("[NotificationService] FCM messaging is not initialized (mock/offline mode).");
        return null;
    }
    if (!('serviceWorker' in navigator)) {
        console.warn("[NotificationService] Service Worker is not supported.");
        return null;
    }
    try {
        // Eski servis çalışanlarını temizle
        await cleanOldServiceWorkers();

        const activeVapidKey = await getVapidPublicKey();

        // Root scope'ta kayıtlı ana Service Worker'ın (/service-worker.js) hazır olmasını bekle
        const registration = await navigator.serviceWorker.ready;

        // Firebase SDK'sına ana SW registration'ını açıkça bildirerek tek SW kullanılmasını sağlıyoruz.
        const token = await getToken(messaging, { 
            vapidKey: activeVapidKey,
            serviceWorkerRegistration: registration
        });
        
        if (!token) {
            console.warn("[NotificationService] getToken() returned empty token. Check VAPID key and SW.");
            return null;
        }
        
        console.log("[NotificationService] FCM Token obtained:", token.substring(0, 20) + '...');
        return token;
    } catch (error: any) {
        console.error("[NotificationService] Error getting FCM Token:", error);
        // Hata teşhisi için Firestore'daki kullanıcı belgesine hatayı yaz
        if (isFirebaseActive && auth.currentUser) {
            const uid = auth.currentUser.uid;
            const userRef = doc(db, "users", uid);
            updateDoc(userRef, {
                lastFcmError: {
                    message: error.message || String(error),
                    code: error.code || 'unknown',
                    timestamp: new Date().toISOString()
                }
            }).catch(e => console.warn("[NotificationService] Could not write error log to Firestore:", e));
        }
        return null;
    }
}


/**
 * Kullanıcıyı tarayıcının Push servisine abone eder ve abonelik nesnesini döner.
 */
export async function subscribeUserToPush(): Promise<any> {
    return await subscribeUserToFCM();
}

/**
 * Push abonelik nesnesini veya FCM tokenini kullanıcının Firestore profil belgesine kaydeder.
 */
// Kayıtlar sırayla yapılır: girişte native + web token aynı anda kaydedilince ikisi de dokümanı okuyup
// üzerine yazıyor, sonra yazan öncekini siliyordu (native token kayboluyordu).
let saveQueue: Promise<unknown> = Promise.resolve();

export function saveSubscriptionToFirestore(subscriptionOrToken: any): Promise<boolean> {
    const run = saveQueue.then(() => saveSubscriptionToFirestoreNow(subscriptionOrToken));
    saveQueue = run.catch(() => {});
    return run;
}

async function saveSubscriptionToFirestoreNow(subscriptionOrToken: any): Promise<boolean> {
    if (!subscriptionOrToken) return false;
    
    let token = "";
    if (typeof subscriptionOrToken === 'string') {
        token = subscriptionOrToken;
    } else if (subscriptionOrToken.endpoint) {
        // if it's a web push subscription object
        token = subscriptionOrToken.endpoint;
    } else {
        token = JSON.stringify(subscriptionOrToken);
    }

    // Offline mod veya giriş yapılmamışsa yerel olarak sakla
    if (!isFirebaseActive || !auth.currentUser) {
        localStorage.setItem('kpss_fcm_token', token);
        console.log("[NotificationService] Abonelik yerel hafızaya kaydedildi (Offline/Ziyaretçi).");
        return true;
    }

    try {
        const uid = auth.currentUser.uid;
        const userRef = doc(db, "users", uid);
        
        // Cihaz ve TWA bilgilerini tespit et
        // isAndroidTwa(): Google giriş yönlendirmesinden sonra referrer değişse de TWA'yı hatırlar
        const isTwa = isAndroidTwa() ||
                      window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone ||
                      document.referrer.includes('android-app://');
        
        const platform = /Android/i.test(navigator.userAgent) ? 'Android' :
                         /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : 'Desktop';

        // Android TWA kabuğunun native FCM token'ı mı? (App.tsx native_fcm_token parametresinden kaydeder)
        let isNative = false;
        try { isNative = !!token && localStorage.getItem('kpss_native_twa_token') === token; } catch {}

        const subscriptionObj = typeof subscriptionOrToken === 'string'
            ? { endpoint: token, isTwa, isNative, platform, updatedAt: new Date().toISOString() }
            : { ...subscriptionOrToken, isTwa, isNative, platform, updatedAt: new Date().toISOString() };
        
        // Mevcut abonelikleri çekip temizleyelim ve limit koyalım (Max 5 cihaz/abonelik)
        const userSnap = await getDoc(userRef);
        let currentSubscriptions: any[] = [];
        let currentTokens: string[] = [];
        if (userSnap.exists()) {
            const userData = userSnap.data();
            currentSubscriptions = userData.pushSubscriptions || [];
            currentTokens = userData.fcmTokens || [];
        }

        // Aynı token zaten varsa filtrele, en başa en güncel haliyle ekle (Max 5 aktif cihaz)
        const filteredTokens = currentTokens.filter((t: string) => t !== token);
        const updatedTokens = [token, ...filteredTokens].slice(0, 5);

        const filteredSubs = currentSubscriptions.filter((s: any) => {
            if (!s) return false;
            const endp = typeof s === 'string' ? s : s.endpoint;
            return endp !== token && !endp?.includes(token);
        });
        const updatedSubscriptions = [subscriptionObj, ...filteredSubs].slice(0, 5);

        // Güncel veriyi yaz
        await updateDoc(userRef, {
            fcmTokens: updatedTokens,
            pushSubscriptions: updatedSubscriptions
        });

        console.log("[NotificationService] Abonelik Firestore'a başarıyla kaydedildi.");
        localStorage.setItem('kpss_fcm_token', token);
        claimPushToken(token);
        return true;
    } catch (error: any) {
        console.error("[NotificationService] Abonelik Firestore'a kaydedilemedi:", error);
        localStorage.setItem('kpss_fcm_token', token);
        if (isFirebaseActive && auth.currentUser) {
            const uid = auth.currentUser.uid;
            const userRef = doc(db, "users", uid);
            updateDoc(userRef, {
                lastFcmError: {
                    message: "Firestore save failed: " + (error.message || String(error)),
                    timestamp: new Date().toISOString()
                }
            }).catch(e => console.warn("[NotificationService] Could not write Firestore save error log:", e));
        }
        return false;
    }
}

/**
 * Bu cihazın token'ını başka hesaplardan sildirir (sunucu: claimPushToken). Aynı cihazda iki hesap açılınca
 * token iki hesapta kalıyor, bildirimler iki kez geliyordu. Token önce kendi dokümanımıza yazılmış olmalı.
 * Oturum başına hesap+token için bir kez; hata sessizce yutulur (sonraki açılışta tekrar denenir).
 */
const claimedTokens = new Set<string>();
export function claimPushToken(token: string): void {
    const user = isFirebaseActive ? auth.currentUser : null;
    if (!user || !token) return;
    const key = `${user.uid}|${token}`;
    if (claimedTokens.has(key)) return;
    claimedTokens.add(key);
    user.getIdToken()
        .then(idToken => fetch(`${BACKEND_URL}/claimPushToken`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({ tokens: [token] })
        }))
        .then(async res => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const body = await res.json().catch(() => ({}));
            if (Array.isArray(body?.dead) && body.dead.includes(token)) await replaceDeadToken(token);
        })
        .catch(err => {
            claimedTokens.delete(key);
            console.warn("[NotificationService] Token sahiplenilemedi:", err);
        });
}

/**
 * Sunucu token'ın ölü olduğunu söyledi (FCM "kayıtlı değil" demişti). Firebase SDK aynı token'ı 7 gün
 * önbellekten vermeye devam ettiği için her açılışta ölü token kaydediliyor, bildirim hiç gelmiyordu.
 * Web token'ında tarayıcının push aboneliği sıfırlanır; SDK yeni abonelikle yeni token alır.
 */
let deadTokenRefreshTried = false;
async function replaceDeadToken(token: string): Promise<void> {
    let isNativeToken = false;
    for (const key of ['kpss_fcm_token', 'kpss_native_twa_token', 'kpss_native_push_token']) {
        try {
            if (localStorage.getItem(key) !== token) continue;
            if (key !== 'kpss_fcm_token') isNativeToken = true;
            localStorage.removeItem(key);
        } catch {}
    }
    // Native (Android uygulaması / iOS) token'ı web'den yenilenemez; uygulama açılışta kendisi yenisini verir
    if (isNativeToken || isNativePlatform() || deadTokenRefreshTried) return;
    if (!('Notification' in window) || Notification.permission !== 'granted' || !('serviceWorker' in navigator)) return;
    deadTokenRefreshTried = true;
    try {
        const registration = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise<null>(resolve => setTimeout(() => resolve(null), 3000))
        ]);
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
        const fresh = await subscribeUserToFCM();
        if (fresh && fresh !== token) {
            await saveSubscriptionToFirestore(fresh);
            console.log("[NotificationService] Ölü push token'ı yenisiyle değiştirildi.");
        }
    } catch (err) {
        console.warn("[NotificationService] Ölü token yenilenemedi:", err);
    }
}

/**
 * Bu cihazın bilinen bütün push token'ları: web FCM, Android TWA native, iOS APNs.
 */
function getDeviceTokens(): string[] {
    const tokens: string[] = [];
    for (const key of ['kpss_fcm_token', 'kpss_native_twa_token', 'kpss_native_push_token']) {
        try {
            const t = localStorage.getItem(key);
            if (t && !tokens.includes(t)) tokens.push(t);
        } catch {}
    }
    return tokens;
}

/**
 * Kullanıcının tarayıcı izinleri verilmişse push aboneliğini arka planda otomatik senkronize eder.
 */
export async function syncPushSubscription() {
    // Native platformda (iOS/Android Capacitor) Web Push kullanma — nativePushService.ts yönetir
    try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
            console.log("[NotificationService] Native platform tespit edildi, Web Push atlanıyor.");
            return;
        }
    } catch (e) {
        // Capacitor yüklü değilse (pure web), devam et
    }

    if (typeof window === 'undefined' || !('Notification' in window)) return;
    
    // Eğer kullanıcı daha önce izin vermemiş veya reddetmemişse (default ise), izni otomatik sor
    if (Notification.permission === 'default') {
        try {
            const permission = await requestNotificationPermission();
            if (permission !== 'granted') {
                console.log("[NotificationService] Otomatik bildirim izni istemi reddedildi.");
                return;
            }
        } catch (e) {
            console.warn("[NotificationService] Otomatik izin isteme sırasında hata:", e);
            return;
        }
    }

    if (Notification.permission !== 'granted') return;

    try {
        const token = await subscribeUserToFCM();
        if (token) {
            await saveSubscriptionToFirestore(token);
            console.log("[NotificationService] Push aboneliği arka planda otomatik senkronize edildi.");
        }
    } catch (error) {
        console.warn("[NotificationService] Otomatik abonelik senkronizasyon hatası:", error);
    }
}

/**
 * Belirli bir gecikme (milisaniye) sonrası arka plan testi için yerel bildirim tetikler.
 */
export function sendLocalTestNotification(delayMs: number = 5000) {
    if (!('serviceWorker' in navigator)) {
        console.warn("Service Worker bulunamadı, yerel bildirim tetiklenemiyor.");
        return;
    }

    setTimeout(async () => {
        try {
            if (typeof window !== 'undefined' && 'Notification' in window) {
                if (Notification.permission !== 'granted') {
                    console.warn("[NotificationService] Bildirim izni verilmemiş, izin isteniyor...");
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                        console.warn("[NotificationService] Kullanıcı bildirim iznini reddetti.");
                        alert("Bildirim izni verilmediği için test bildirimi gösterilemiyor. Lütfen tarayıcı ayarlarından bildirim izinlerini etkinleştirin.");
                        return;
                    }
                }
            }
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification("Kapı Hoca ile KPSS", {
                body: "🦅 Harika gidiyorsun! Bugün Coğrafya harita çalışmasını tamamlamayı unutma.",
                icon: "/icon-192.png",
                badge: "/favicon.png",
                vibrate: [200, 100, 200],
                tag: "kpss-study-reminder",
                renotify: true,
                data: { url: '/' }
            } as any);
            console.log("[NotificationService] Yerel test bildirimi tetiklendi.");
        } catch (error) {
            console.error("[NotificationService] Yerel bildirim gösterilirken hata:", error);
        }
    }, delayMs);
}

/**
 * Kullanıcı çıkış yaparken bu cihazdaki push aboneliğini Firestore'dan temizler.
 * TWA veya bazı mobil tarayıcılarda serviceWorker askıda kalabildiği için 2 sn timeout eklenmiştir.
 */
export async function removePushSubscriptionOnLogout() {
    if (!isFirebaseActive || !auth.currentUser) return;

    try {
        const uid = auth.currentUser.uid;
        const userRef = doc(db, "users", uid);
        // Bu cihazın bütün token'ları silinir (eskiden yalnızca kpss_fcm_token; TWA native token hesapta kalıyordu)
        const deviceTokens = getDeviceTokens();

        if ('serviceWorker' in navigator && 'PushManager' in window) {
            // TWA/Mobil tarayıcılarda ready promise'inin sonsuza kadar kilitlenmesini önlemek için timeout yarışı yapıyoruz
            const subscription = await Promise.race([
                (async () => {
                    const registration = await navigator.serviceWorker.ready;
                    return await registration.pushManager.getSubscription();
                })(),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000))
            ]);
            if (subscription?.endpoint && !deviceTokens.includes(subscription.endpoint)) deviceTokens.push(subscription.endpoint);
        }
        if (deviceTokens.length === 0) return;

        // arrayRemove({ endpoint }) kullanma: kayıtlı nesnede platform/isTwa/updatedAt da var, eşleşmez ve abonelik
        // hesapta kalıyordu (aynı cihazdaki sonraki hesapla birlikte çift bildirim). Uç noktaya göre süzülür.
        const snap = await getDoc(userRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const isDeviceEndpoint = (e: unknown) => typeof e === 'string' && deviceTokens.some(t => e === t || e.includes(t));
        const tokens: string[] = data.fcmTokens || [];
        const subs: any[] = data.pushSubscriptions || [];
        const nextTokens = tokens.filter(t => !isDeviceEndpoint(t));
        const nextSubs = subs.filter(s => s && !isDeviceEndpoint(typeof s === 'string' ? s : s.endpoint));
        if (nextTokens.length !== tokens.length || nextSubs.length !== subs.length) {
            await updateDoc(userRef, { fcmTokens: nextTokens, pushSubscriptions: nextSubs });
            console.log("[NotificationService] Bu cihazın push token'ları çıkışta hesaptan silindi.");
        }
        // Token'lar localStorage'da kalır: sonraki girişte yeni hesaba yazılır ve sahiplenilir (claimPushToken)
    } catch (error) {
        console.warn("[NotificationService] Failed to remove push subscription on logout:", error);
    }
}

/**
 * Backend üzerinden (FCM/Web Push) test bildirimi gönderilmesini tetikler.
 * 5 saniye sonra tetikleneceği için kullanıcı uygulamayı tamamen kapatsa dahi bildirim ulaşacaktır.
 */
export async function sendBackendTestNotification(): Promise<boolean> {
    if (!isFirebaseActive || !auth.currentUser) {
        console.warn("[NotificationService] Giriş yapılmamış veya Firebase aktif değil, test bildirimi gönderilemiyor.");
        return false;
    }

    try {
        const token = await auth.currentUser.getIdToken();
        const response = await fetch(`${BACKEND_URL}/sendTestPush`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (response.ok) {
            console.log("[NotificationService] Backend test bildirimi başarıyla kuyruğa alındı.");
            return true;
        } else {
            const errText = await response.text();
            console.error("[NotificationService] Backend test bildirimi başarısız oldu:", response.status, errText);
            return false;
        }
    } catch (error) {
        console.error("[NotificationService] Backend test bildirimi çağrılırken hata:", error);
        return false;
    }
}

